import { AuthService } from '../../../services/auth/auth.service';
import { inject, Component, OnInit } from '@angular/core';
import { CacheAdminService, ServiceCacheKeys } from '../../../services/cache-admin.service';
import { MarketplaceCategoryService } from '../../../services/marketplace-category.service';

// Cache console: inspect and evict cached data across every platform service — this API, the POS,
// Inventory, marketplace API and storefront. The listing loads on open with no search term, so the
// full picture is visible without having to guess a key first. Search narrows it server-side;
// "Clear matching"/"Clear all caches" evict across every service in one call.
@Component({
  selector: 'app-cache-page',
  standalone: false,
  templateUrl: './cache-page.component.html',
  styleUrls: ['./cache-page.component.css']
})
export class CachePageComponent implements OnInit {
  readonly staffAuth = inject(AuthService);
  search = '';
  services: ServiceCacheKeys[] = [];
  totalKeys = 0;
  loaded = false;
  busy = false;
  message = '';
  error = '';

  /** Optional business id scoping "Force clear terminal caches"; blank = every terminal. */
  purgeBusinessId = '';

  /** Services whose key list is expanded. Collapsed by default — some hold hundreds of keys. */
  private expanded = new Set<string>();

  /**
   * Storyblok responses are cached in the marketplace API as `Storyblok_{slug}`, sliding 10 days in
   * production. That expiry is why a CMS edit can take days to reach the site, so it gets a
   * dedicated button rather than making an admin remember the key prefix.
   */
  readonly cmsKeyPrefix = 'Storyblok';

  constructor(
    private cacheAdmin: CacheAdminService,
    private categories: MarketplaceCategoryService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  /** Load the platform-wide listing. An empty search lists every key in every service. */
  load(): void {
    this.busy = true;
    this.error = '';
    this.cacheAdmin.listAll(this.search.trim() || undefined).subscribe({
      next: result => {
        this.services = result?.services ?? [];
        this.totalKeys = result?.totalKeys ?? 0;
        this.loaded = true;
        this.busy = false;
      },
      error: () => {
        this.error = 'Could not list caches. The account API may be unreachable.';
        this.busy = false;
      }
    });
  }

  clearMatching(): void {
    if (!this.staffAuth.can('Platform.Cache.Purge')) return;
    const term = this.search.trim();
    if (!term) {
      this.error = 'Enter a search term first — or use "Clear all caches".';
      return;
    }
    if (!window.confirm(`Clear every cache entry matching '${term}' across all services?`)) {
      return;
    }
    this.runClear(term);
  }

  clearAll(): void {
    if (!this.staffAuth.can('Platform.Cache.Purge')) return;
    if (!window.confirm('Clear EVERY cache in EVERY service? All cached values are refetched on next use.')) {
      return;
    }
    this.runClear(undefined);
  }

  /**
   * Force terminals to delete their LOCAL cached data (IndexedDB + legacy web-storage caches) —
   * distinct from the buttons above, which clear SERVER memory caches. Connected terminals purge
   * over SignalR immediately; closed/offline ones purge on next launch via the recorded stamp.
   */
  purgeTerminals(): void {
    if (!this.staffAuth.can('Platform.Cache.Purge')) return;
    const reason = window.prompt('Reason for the terminal cache purge (required):')?.trim();
    if (!reason) return;
    const businessId = this.purgeBusinessId.trim() || undefined;
    const scopeText = businessId
      ? `every terminal of business '${businessId}'`
      : 'EVERY terminal on the platform';
    if (!window.confirm(`Force ${scopeText} to delete its locally cached data? Terminals re-download as screens are used; in-progress sales are not affected.`)) {
      return;
    }
    this.busy = true;
    this.message = '';
    this.error = '';
    this.cacheAdmin.purgeTerminals(businessId, reason).subscribe({
      next: result => {
        this.message = `Terminal cache purge issued for ${result?.scope ?? 'all businesses'}. Online terminals purge now; offline ones on next launch.`;
        this.busy = false;
      },
      error: () => {
        this.error = 'Could not issue the terminal cache purge.';
        this.busy = false;
      }
    });
  }

  /** Evict cached CMS content so a Storyblok edit shows up without waiting out the sliding expiry. */
  flushCms(): void {
    if (!this.staffAuth.can('Platform.Cache.Purge')) return;
    if (!window.confirm('Flush cached Storyblok CMS content across the platform? Pages are refetched from Storyblok on next request.')) {
      return;
    }
    this.runClear(this.cmsKeyPrefix);
  }

  private runClear(term: string | undefined): void {
    if (!this.staffAuth.can('Platform.Cache.Purge')) return;
    const reason = window.prompt('Reason for clearing caches (required):')?.trim();
    if (!reason) return;
    this.busy = true;
    this.message = '';
    this.error = '';
    this.cacheAdmin.clearAll(term, reason).subscribe({
      next: result => {
        const perService = (result?.services ?? [])
          .map(s => {
            if (s.configured === false) { return `${s.service}: not configured`; }
            return s.reachable ? `${s.service}: ${s.cleared}` : `${s.service}: unreachable`;
          })
          .join(' · ');
        this.message = `Cleared ${result?.totalCleared ?? 0} entr${result?.totalCleared === 1 ? 'y' : 'ies'} (${perService}).`;

        // This portal caches the category list in localStorage with no TTL, so it would otherwise
        // survive a platform-wide flush and keep showing the pre-clear data.
        this.categories.clearCache();

        this.busy = false;
        this.load();
      },
      error: () => {
        this.error = 'Could not clear the caches.';
        this.busy = false;
      }
    });
  }

  /**
   * Per-key ✕: evict exactly one entry from one service. No confirm — a single stale
   * key re-warms on next use, and the whole point is removing entries individually
   * without the blast radius of "Clear matching".
   */
  evictKey(svc: ServiceCacheKeys, key: string): void {
    if (!this.staffAuth.can('Platform.Cache.Evict')) return;
    if (this.busy) return;
    this.busy = true;
    this.message = '';
    this.error = '';
    this.cacheAdmin.evictKey(svc.service, key).subscribe({
      next: result => {
        if (result?.reachable && result.cleared > 0) {
          svc.keys = svc.keys.filter(k => k !== key);
          this.totalKeys = Math.max(0, this.totalKeys - 1);
          this.message = `Evicted '${key}' from ${svc.service}.`;
        } else if (result?.reachable) {
          // Already gone (expired or evicted elsewhere) — just drop it from the view.
          svc.keys = svc.keys.filter(k => k !== key);
          this.totalKeys = Math.max(0, this.totalKeys - 1);
          this.message = `'${key}' was already gone from ${svc.service}.`;
        } else {
          this.error = `Could not reach ${svc.service}: ${result?.error ?? 'unknown error'}`;
        }
        this.busy = false;
      },
      error: () => {
        this.error = 'Could not evict the entry.';
        this.busy = false;
      }
    });
  }

  toggle(service: string): void {
    if (this.expanded.has(service)) {
      this.expanded.delete(service);
    } else {
      this.expanded.add(service);
    }
  }

  isExpanded(service: string): boolean {
    return this.expanded.has(service);
  }

  /** Services that answered, for the summary line. */
  get reachableCount(): number {
    return this.services.filter(s => s.reachable).length;
  }

  /**
   * Services declared on the account API but with no base URL set, so their cache cannot be
   * reached from here at all. Called out separately because it is fixed by configuration
   * (Caching__Targets__<Name>), not by retrying.
   */
  get unconfigured(): ServiceCacheKeys[] {
    return this.services.filter(s => s.configured === false);
  }
}
